"use client"

import { PartnerHospitalDetailImage } from "@/entities/hospital/api/types"
import { Typography } from "@/shared/ui/atoms"
import Image from "next/image"
import { DirectionIcon } from "@/shared/ui/atoms/SVGIcons/BaseIcons"
import Link from "next/link"
import { useParams } from "next/navigation"
import { useMemo, useState } from "react"
import { Modal } from "@/shared/ui/templates"
import { Swiper, SwiperSlide } from "swiper/react"
import { useTranslations } from "next-intl"
import { useHospitalOriginId } from "@/entities/hospital/model/hospital-origin-id"
import { buildHospitalShortPath } from "@/entities/hospital/lib/detail-route"

interface HospitalImagesProps {
  imageList: PartnerHospitalDetailImage[]
  /**
   * 새 상세 페이지의 섹션 셸 안에 들어갈 때. 제목과 바깥 여백은 섹션이 갖고 있으므로
   * 여기서는 내용만 그린다. 기본값(false)은 기존 상세 페이지의 모습 그대로다.
   */
  embedded?: boolean
}

const HospitalImages = ({ imageList, embedded = false }: HospitalImagesProps) => {
  const t = useTranslations()
  const params = useParams()
  const locale = (params as { locale?: string })?.locale
  // 라우트에 [id] 가 없는 상세(지역·진료과 URL)에서도 같은 병원을 가리켜야 한다
  const id = useHospitalOriginId()

  const href = locale && Number.isFinite(id) ? buildHospitalShortPath(locale, id) + "/images" : "images"
  const [isViewerOpen, setIsViewerOpen] = useState(false)
  const [viewerIndex, setViewerIndex] = useState(0)

  const validImages = useMemo(() => imageList.filter((img) => img.url?.trim()), [imageList])

  return (
    <div
      id={embedded ? undefined : "hospital-images"}
      className={embedded ? "flex flex-col" : "flex flex-col pt-1.5 pb-[30px]"}
    >
      {embedded ? null : (
        <div className="py-3 px-4 flex items-center justify-between">
          <Typography size={18} className="text-gray-900 font-bold">
            {t("hospitalImages.title")}
          </Typography>
          <Link href={href} className="inline-flex">
            <Typography size={14} className="flex items-center text-gray-600">
              {t("common.more")} <DirectionIcon size={16} rotate={270} className="text-gray-400" />
            </Typography>
          </Link>
        </div>
      )}
      <div className="flex gap-2 overflow-x-auto scrollbar-hide">
        {validImages.map((image, index) => (
          <button
            key={`hospital-image-${index}`}
            type="button"
            className="relative dynamic-size-min first:ml-4 last:mr-4"
            style={{ minWidth: "154px", height: "190px" }}
            onClick={() => {
              setViewerIndex(index)
              setIsViewerOpen(true)
            }}
          >
            <Image
              src={image.url}
              alt={t("hospitalImages.imageAlt")}
              fill
              className="object-cover rounded-[8px]"
              sizes="(max-width: 600px) 21.33vw, 154px"
            />
          </button>
        ))}
      </div>

      <Modal
        open={isViewerOpen}
        onClose={() => setIsViewerOpen(false)}
        height="screen"
        round="0px"
        headerPaddingTopClassName="pt-4"
        headerMarginBottomClassName="mb-4"
      >
        <div className="h-full w-full">
          <Swiper
            className="h-full w-full"
            initialSlide={viewerIndex}
            loop={validImages.length > 1}
            onSlideChange={(swiper) => {
              setViewerIndex(swiper.realIndex ?? swiper.activeIndex ?? 0)
            }}
          >
            {validImages.map((image, index) => (
              <SwiperSlide key={`hospital-image-viewer-${index}`} className="!h-full">
                <div className="relative h-full w-full">
                  <Image
                    src={image.url}
                    alt={t("hospitalImages.viewerAlt")}
                    fill
                    sizes="(max-width: 600px) 100vw, 600px"
                    className="object-contain"
                    priority={index === viewerIndex}
                  />
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      </Modal>
    </div>
  )
}

export default HospitalImages